import { useEffect, useMemo, useState } from 'react';
import type { MatchCategory, SearchResults } from '@peruselab/api-client/moleculeSearch';
import {
  ALL_CATEGORIES,
  ALL_TYPES,
  mergeByMolecule,
  reactionCards,
  drawingCards,
  documentCards,
  foldedCount,
  placeDrawingCards,
  resultSources,
  sortCards,
  toggleInSet,
  type EntityType,
  type ResultEntry,
  type ResultsWithMatchedBy,
  type SortKey
} from '../components/panels/searchResultsModel';
import { useMoleculeFormulas } from './useMoleculeFormulas';

// Client-side "show more" over the full result set. `POST /api/search/molecules` returns every
// match unpaginated (#116); typical filtered result sets are small, so paging on the client is
// enough for now. Server-side pagination is a separate backend issue if result volumes grow.
const INITIAL_VISIBLE = 5;
const VISIBLE_STEP = 10;

export interface SearchResultFilters {
  readonly categories: ReadonlySet<MatchCategory>;
  readonly types: ReadonlySet<EntityType>;
  readonly minScore: number;
  readonly sortKey: SortKey;
  readonly visible: number;
  readonly toggleCategory: (category: MatchCategory) => void;
  readonly toggleType: (type: EntityType) => void;
  readonly setMinScore: (value: number) => void;
  readonly setSortKey: (key: SortKey) => void;
  readonly showMore: () => void;
  // Derived from the current results + the filters above -- one flat, score-sorted stream
  // mixing every entity type together (the drawing-paper + result-card mosaic layout), not
  // grouped by type or pulled apart by Identical/Similar category. A card's own CategoryBadge is
  // the only category signal now; there is no separate "Identical match" lead block.
  readonly allCards: readonly ResultEntry[];
  readonly rawCount: number;
  /** #807 R3 -- there is a current result set (a search has answered), even an empty one. */
  readonly hasResults: boolean;
  readonly visibleCount: number;
  readonly hiddenCount: number;
  /** #932 -- copies of a structure folded into the molecule cards shown. */
  readonly foldedCount: number;
  readonly hasMore: boolean;
}

// The search-results filter state (match type / entity type / minimum similarity / sort) plus
// its derivation over the current result set. Shared so the filter rail can live in the
// FILTER tab while the result list lives elsewhere (ADR 0007 P6b).
export function useSearchResultFilters(currentResults: SearchResults | null): SearchResultFilters {
  const [categories, setCategories] = useState<ReadonlySet<MatchCategory>>(new Set(ALL_CATEGORIES));
  const [types, setTypes] = useState<ReadonlySet<EntityType>>(new Set(ALL_TYPES));
  const [minScore, setMinScore] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('scoreDesc');
  const [visible, setVisible] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [currentResults, categories, types, minScore, sortKey]);

  // Resolve card names (ADR 0007 P6d); `formulas` fills in asynchronously, so the cards (and the
  // sort, which orders by `label`) recompute as it does.
  const formulas = useMoleculeFormulas(currentResults);
  const sources = useMemo(() => (currentResults ? resultSources(currentResults) : null), [currentResults]);
  const matchedByText = (currentResults as ResultsWithMatchedBy | null)?.matchedByText;

  // One flat, score-sorted stream -- the mosaic layout has no per-type grouping and no separate
  // Identical lead block. #688: the filters apply to each source (the molecule itself, or a
  // drawing / reaction / illustration containing it); what passes is merged into one card per
  // molecule, so a card stays while any of its sources is shown. #743: each reaction found is also
  // a card of its own, in the same stream.
  const drawingsOnly = types.size === 1 && types.has('drawings');
  const allCards = useMemo<readonly ResultEntry[]>(() => {
    if (!sources) return [];
    const passing = sources.filter(
      // #1161: a same-skeleton match shows no score, so the score filter leaves it be.
      (source) =>
        types.has(source.kind) &&
        categories.has(source.category) &&
        (source.category === 'SAME_SKELETON' || source.score >= minScore)
    );
    const sorted = sortCards<ResultEntry>(
      [
        ...mergeByMolecule(passing, formulas, matchedByText),
        ...reactionCards(passing, formulas),
        ...documentCards(passing)
      ],
      sortKey
    );
    // #794 -- with Show narrowed to Drawings alone, drawings that are more than their molecule get
    // cards of their own, each right after its molecule's card.
    const direct = new Set(
      passing.filter((source) => source.standalone && source.kind === 'drawings').map((source) => source.id)
    );
    return placeDrawingCards(
      sorted,
      drawingCards(passing).filter((card) => drawingsOnly || direct.has(card.drawingId))
    );
  }, [sources, formulas, matchedByText, categories, types, minScore, sortKey, drawingsOnly]);

  // Counted in cards, like visibleCount: one per molecule, plus one per reaction (#743).
  const rawCount = sources
    ? new Set(sources.filter((source) => !source.standalone).map((source) => source.moleculeId)).size +
      new Set(sources.filter((source) => source.kind === 'reactions').map((source) => source.id)).size +
      new Set(
        sources
          .filter(
            (source) => source.kind === 'drawings' && source.hasAnnotations && (drawingsOnly || source.standalone)
          )
          .map((source) => source.id)
      ).size +
      new Set(
        sources.filter((source) => source.kind === 'illustrations' && source.standalone).map((source) => source.id)
      ).size
    : 0;
  const visibleCount = allCards.length;
  const hiddenCount = rawCount - visibleCount;
  const hasMore = allCards.length > visible;
  const folded = useMemo(() => foldedCount(allCards), [allCards]);

  return {
    categories,
    types,
    minScore,
    sortKey,
    visible,
    toggleCategory: (category) => setCategories((current) => toggleInSet(current, category)),
    toggleType: (type) => setTypes((current) => toggleInSet(current, type)),
    setMinScore,
    setSortKey,
    showMore: () => setVisible((v) => v + VISIBLE_STEP),
    allCards,
    rawCount,
    hasResults: currentResults !== null,
    visibleCount,
    hiddenCount,
    foldedCount: folded,
    hasMore
  };
}
